import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "react-datepicker/dist/react-datepicker.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store from "./store/store";
import { BrowserRouter as Router } from "react-router-dom";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// msalInstance.initialize().then(() => {
// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

// msalInstance.addEventCallback((event) => {
//   if (
//     (event.eventType === EventType.LOGIN_SUCCESS ||
//       event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
//       event.eventType === EventType.SSO_SILENT_SUCCESS) &&
//     event.payload.account
//   ) {
//     msalInstance.setActiveAccount(event.payload.account);
//   }
// });

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});

// Initialize Datadog RUM
const applicationId = process.env.REACT_APP_DD_APP_ID;
const clientToken = process.env.REACT_APP_DD_CLIENT_TOKEN;
if (!applicationId || !clientToken) {
  if (process.env.REACT_APP_DD_ENV !== 'prod') {
    console.log('Datadog RUM initialization failed: Missing applicationId or clientToken', { applicationId, clientToken });  // Debug; remove in prod
  }
} else {
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'us3.datadoghq.com',
    service: process.env.REACT_APP_DD_SERVICE_NAME,
    env: process.env.REACT_APP_DD_ENV,
    sessionSampleRate: parseInt(process.env.REACT_APP_DD_SESSION_SAMPLE_RATE) || 100,
    sessionReplaySampleRate: parseInt(process.env.REACT_APP_DD_SESSION_REPLAY_SAMPLE_RATE) || 12, // budget: ~5,022 replays/mo against the 10k Session Replay commit
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackErrors: true,
    version: process.env.REACT_APP_VERSION || '1.0.0',
    plugins: [reactPlugin({ router: true })],
    beforeSend: (event, context) => {
      // if (process.env.REACT_APP_DD_ENV !== 'prod') {
      //   console.log('RUM Event:', event);  // Debug; remove in prod
      // }
      if (!event || typeof event !== 'object' || (Array.isArray(event) && event.length > 1000)) {
        if (process.env.REACT_APP_DD_ENV !== 'prod') {
          console.warn('Invalid event detected, skipping:', event);
        }
        return false;
      }
      return true;
    },
  });
  if (process.env.REACT_APP_DD_ENV !== 'prod') {
    console.log('Datadog RUM Initialized', { appId: applicationId });  // Debug; remove in prod
  }
  datadogRum.startSessionReplayRecording();  // Ensure replay starts
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <Router>
        <App instance={msalInstance} />
      </Router>
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
// });
